import { Schedule } from "../../store/interfaces";
import {
	AppState,
	setHeaderState,
	setIsDownloadPDFButtonEnable,
	setPageState,
	setTriggerDownloadPDF,
} from "../../store/reducer";
import { useDispatch, useSelector } from "react-redux";
import { AreaItem, HeaderState, HospitalItem } from "../Header/HeaderUtils";
import { useEffect, useMemo, useState } from "react";
import { getComparator, Order } from "../../utils/orderTable";
import { createRow, Row } from "./DashboardUtils";
import { getHeaders, URI_API } from "../../utils/config";
import { Dispatch } from "redux";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";

import { DashboardPDF, getImageByHospital } from "./DashboardPDF";
import { PageState } from "../../views/Home/HomeUtils";
import { format } from "date-fns";

export function useDashboard() {
	const selectedAreaId = useSelector((state: AppState) => state.selectedAreaId);
	const userSchedule: Schedule | undefined = useSelector(
		(state: AppState) => state.loggedUser,
	).schedule;
	const triggerDownloadPDF: boolean = useSelector((state: AppState) => state.triggerDownloadPDF);
	const selectedArea: AreaItem | undefined = useSelector((state: AppState) => state.selectedArea);
	const selectedHospital: HospitalItem | undefined = useSelector(
		(state: AppState) => state.selectedHospital,
	);

	const dispatch: Dispatch<any> = useDispatch();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [order, setOrder] = useState<Order>("asc");
	const [orderBy, setOrderBy] = useState<keyof Row>("room");
	const [rows, setRows] = useState<any[]>([]);

	const onChangeSort = (event: React.MouseEvent<unknown>, property: keyof Row) => {
		const isAsc = orderBy === property && order === "asc";
		setOrder(isAsc ? "desc" : "asc");
		setOrderBy(property);
	};

	const visibleRows = useMemo(
		() => [...rows].sort(getComparator(order, orderBy)),
		[rows, order, orderBy],
	);

	const filteredRows = visibleRows.filter((row: Row) => row.areaId === selectedAreaId);

	const getAssignedNurses = async (): Promise<void> => {
		fetch(`${URI_API}/assigned_nurses`, getHeaders())
			.then((res) => res.json())
			.then((body) => {
				const filteredRecords: { [key: string]: Row & { date_of_admission: string } } = {};

				Object.entries(body).forEach(([id, details]) => {
					const {
						room,
						category,
						nurse_name,
						nurse_classx,
						secondary_nurse_name,
						secondary_nurse_classx,
						area_id,
						date_of_admission,
					} = details as {
						room: string;
						category: string;
						nurse_name: string;
						nurse_classx: string;
						secondary_nurse_name: string;
						secondary_nurse_classx: string;
						area_id: string;
						date_of_admission: string;
					};

					if (!room || room.trim() === "") {
						return;
					}

					const newRow: Row = createRow(
						room,
						category,
						nurse_name,
						nurse_classx,
						secondary_nurse_name,
						secondary_nurse_classx,
						area_id,
						date_of_admission,
					);

					const key = `${area_id}_${room}`;

					if (!(key in filteredRecords)) {
						filteredRecords[key] = { ...newRow, date_of_admission };
					} else {
						const existingDate = new Date(filteredRecords[key].date_of_admission);
						const currentDate = new Date(date_of_admission);

						if (currentDate > existingDate) {
							filteredRecords[key] = { ...newRow, date_of_admission };
						} else {
						}
					}
				});

				const newRows: Row[] = Object.values(filteredRecords).map(
					({ date_of_admission, ...row }) => row,
				);

				setRows([...newRows]);
				setIsLoading(false);
			});
	};

	useEffect(() => {
		getAssignedNurses();
	}, []);

	useEffect(() => {
		if (filteredRows.length > 0) dispatch(setIsDownloadPDFButtonEnable(true));
		else dispatch(setIsDownloadPDFButtonEnable(false));
	}, [filteredRows]);

	const downloadPdf = async () => {
		if (!selectedArea || !selectedHospital) return;

		const formattedDate = format(new Date(), "dd-MM-yyyy");
		const fileName: string = `${
			selectedArea.name
		}_${formattedDate}_${userSchedule?.name.toUpperCase()}`;
		await pdf(
			<DashboardPDF
				fileName={fileName}
				area={selectedArea.name}
				imageURL={getImageByHospital(selectedHospital)}
				data={filteredRows}
				schedule={`Turno ${userSchedule?.name.toUpperCase()} [${userSchedule?.entrance} - ${
					userSchedule?.departure
				}]`}
			/>,
		)
			.toBlob()
			.then((blob) => saveAs(blob, `${fileName}.pdf`));

		dispatch(setTriggerDownloadPDF(false));
	};

	useEffect(() => {
		if (triggerDownloadPDF) downloadPdf();
	}, [triggerDownloadPDF]);

	const navigateToPatients = () => {
		dispatch(setHeaderState(HeaderState.Normal));
		dispatch(setPageState(PageState.Patients));
	};

	return {
		isLoading,
		order,
		orderBy,
		rows: filteredRows,
		onChangeSort,
		navigateToPatients,
	};
}
